import PropTypes from 'prop-types';

const SIZES = ['1920', '1280', '768', '480'];

const TONES = {
    // Matches the other tile action icons (preview, replace, crop…).
    chrome: 'p-1 text-gray-400 hover:text-gray-600 transition-colors',
    // On a photo when the masonry is not being edited.
    onImage: 'pointer-events-auto p-1.5 text-white [filter:drop-shadow(0_1px_2px_rgba(0,0,0,0.55))] opacity-80 transition-opacity hover:opacity-100',
};

function withSlash(url) {
    if (!url) return '';
    const value = String(url);
    return /^(https?:)?\/\//.test(value) || value.startsWith('/') || value.startsWith('data:') ? value : `/${value}`;
}

/** The uploaded original (a video's video file), or the largest size there is. */
export function originalDownloadUrl(file) {
    if (!file || typeof file !== 'object') return '';
    const url = file.url || {};
    if (typeof url === 'string') return withSlash(url);
    // A video's url.original is its poster still.
    if (url.video) return withSlash(url.video);
    if (url.original) return withSlash(url.original);
    const srcset = file.srcset || url.srcset || {};
    for (const size of SIZES) {
        if (srcset[size]) return withSlash(srcset[size]);
    }
    return withSlash(url.xl || url.lg || url.md || url.sm || '');
}

function downloadName(file, href) {
    const named = file?.originalName || file?.originalname || file?.filename || file?.name;
    if (named) return String(named);
    try {
        return decodeURIComponent(new URL(href, 'http://x').pathname.split('/').pop() || 'image');
    } catch {
        return 'image';
    }
}

function saveFile(href, name) {
    const link = document.createElement('a');
    link.href = href;
    link.download = name;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
}

/**
 * Icon that saves an image's original file. A button so it shares colour with
 * the other tile actions (an <a> picked up the page link colour).
 */
export default function DownloadOriginalButton({
    file,
    tone = 'chrome',
    className,
    title = 'Download original',
}) {
    const href = originalDownloadUrl(file);
    if (!href) return null;
    const name = downloadName(file, href);
    return (
        <button
            type="button"
            tabIndex={-1}
            className={className || TONES[tone] || TONES.chrome}
            title={title}
            aria-label={title}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                saveFile(href, name);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
        >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M12 4v12m0 0l-4-4m4 4l4-4" />
            </svg>
        </button>
    );
}

DownloadOriginalButton.propTypes = {
    file: PropTypes.object,
    tone: PropTypes.oneOf(['chrome', 'onImage']),
    className: PropTypes.string,
    title: PropTypes.string,
};
